// STT / TTS provider switch — 本地 ←→ Cloud
import { useState, useEffect } from 'react';
import { Cpu, Cloud, Loader2 } from 'lucide-react';
import { apiFetch } from '../services/apiClient';
import { toast } from 'sonner';

export function VoiceProviderSwitch({ t }: { t?: any }) {
  const [pref, setPref] = useState<{ stt: string; tts: string }>({ stt: 'auto', tts: 'auto' });
  const [active, setActive] = useState<{ stt: string; tts: string }>({ stt: '?', tts: '?' });
  const [saving, setSaving] = useState(false); // Bug 17: 添加 saving 状态

  const load = () => {
    apiFetch('/api/voice/active-provider')
      .then(r => r.json())
      .then(d => { setPref(d.pref); setActive(d.active); })
      .catch((err) => { console.warn('Failed to load voice provider:', err?.message || err); }); // Bug 37: 添加 console.warn
  };
  useEffect(() => { load(); }, []);

  const save = async (stt: string, tts: string) => {
    setSaving(true);
    try {
      await apiFetch('/api/voice/provider', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stt, tts }),
      });
      load();
    } catch (err) {
      console.error('Failed to save voice provider:', err);
      toast.error(t?.saveFailed || '保存语音提供商配置失败'); // Bug 17: 添加错误处理
    } finally {
      setSaving(false);
    }
  };

  const sttOpts = [
    { value: 'auto', label: t?.auto || '自动' },
    { value: 'local-whisper', label: t?.local || '本地' },
    { value: 'ark', label: 'Doubao' },
    { value: 'qwen', label: 'Qwen ASR' },
    { value: 'deepgram', label: 'Deepgram' },
    { value: 'whisper', label: 'Whisper' },
  ];

  const ttsOpts = [
    { value: 'auto', label: t?.auto || '自动' },
    { value: 'gptsovits', label: t?.local || '本地' },
    { value: 'ark', label: 'Doubao' },
    { value: 'cosyvoice', label: 'CosyVoice' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-black uppercase tracking-widest text-white/40">{t?.sttProvider || 'STT'}</span>
        <div className="flex items-center gap-1">
          {active.stt === 'local-whisper' ? <Cpu size={12} className="text-emerald-400" /> : <Cloud size={12} className={active.stt === 'ark' ? 'text-cyan-400' : 'text-blue-400'} />}
          <span className="text-[12px] font-mono text-white/55">{active.stt}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {sttOpts.map(o => (
          <button
            key={o.value}
            disabled={saving} // Bug 17: 禁用按钮防止重复提交
            onClick={() => save(o.value, pref.tts)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              pref.stt === o.value
                ? 'bg-celestial-saturn text-black'
                : 'bg-white/5 text-white/40 hover:bg-white/10'
            }`}
          >{o.label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-xs font-black uppercase tracking-widest text-white/40">{t?.ttsProvider || 'TTS'}</span>
        <div className="flex items-center gap-1">
          {active.tts === 'gptsovits' ? <Cpu size={12} className="text-emerald-400" /> : <Cloud size={12} className={active.tts === 'ark' ? 'text-cyan-400' : 'text-blue-400'} />}
          <span className="text-[12px] font-mono text-white/55">{active.tts}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {ttsOpts.map(o => (
          <button
            key={o.value}
            disabled={saving} // Bug 17: 禁用按钮防止重复提交
            onClick={() => save(pref.stt, o.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              pref.tts === o.value
                ? 'bg-celestial-saturn text-black'
                : 'bg-white/5 text-white/40 hover:bg-white/10'
            }`}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );
}
